import { useEffect, useState } from "react";
import { Check, Loader2, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { track } from "@/lib/posthog";
import { isDesignShared, shareDesign, unshareDesign } from "@/lib/feed";

interface Props {
  designId: string;
  imageUrl: string;
  style?: string | null;
}

const ShareToFeedButton = ({ designId, imageUrl, style }: Props) => {
  const { toast } = useToast();
  const [shared, setShared] = useState<boolean | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    isDesignShared(designId).then((s) => active && setShared(s));
    return () => {
      active = false;
    };
  }, [designId]);

  const confirmShare = async () => {
    setBusy(true);
    try {
      await shareDesign(designId);
      setShared(true);
      setOpen(false);
      track("feed_shared", { design_id: designId, style: style ?? undefined });
      toast({ title: "Shared to Inspiration", description: "Other Huelo members can now see this design." });
    } catch {
      toast({ title: "Couldn't share", description: "Please try again in a moment.", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await unshareDesign(designId);
      setShared(false);
      track("feed_unshared", { design_id: designId });
      toast({ title: "Removed from Inspiration", description: "This design won't be shared." });
    } catch {
      toast({ title: "Couldn't remove", description: "Please try again.", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  if (shared === null) return null;

  return (
    <>
      <div className="w-full rounded-lg border border-border bg-muted/50 p-3 sm:w-auto sm:max-w-xl">
        <div className="flex items-center justify-between gap-3">
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground">
            {shared ? <Check className="h-4 w-4 text-primary" /> : <Share2 className="h-4 w-4" />}
            {shared ? "Shared to feed" : "Not shared to feed"}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={shared ? remove : () => setOpen(true)}
            disabled={busy}
            className="h-8 shrink-0 px-3 text-xs"
          >
            {busy ? "Saving…" : shared ? "Don't share" : "Share"}
          </Button>
        </div>
        <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
          Only your final image and furniture options are shared. No personal info is ever shared.
        </p>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Share to Inspiration?</DialogTitle>
            <DialogDescription>
              Only the final design and its furniture suggestions are shared. Your room photo, name, email, answers and
              budget are never shown. You can stop sharing anytime.
            </DialogDescription>
          </DialogHeader>
          <img src={imageUrl} alt="Your design" className="w-full rounded-lg aspect-[4/3] object-cover" />
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              className="rounded-full px-4 py-2 text-xs font-semibold text-muted-foreground hover:text-foreground"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmShare}
              disabled={busy}
              className="inline-flex items-center justify-center gap-1.5 rounded-full bg-primary text-primary-foreground px-5 py-2 text-xs font-semibold hover:opacity-90 disabled:opacity-60"
            >
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Share
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ShareToFeedButton;
